import React from "react";

import { CircularProgress } from "@mui/material";
import Highcharts from "highcharts";
import HighchartsReact, { HighchartsReactRefObject } from "highcharts-react-official";
import exportData from "highcharts/modules/export-data";
import exporting from "highcharts/modules/exporting";
import fullscreen from "highcharts/modules/full-screen";
import offlineExporting from "highcharts/modules/offline-exporting";
import { useFormatNumericHook } from "hooks";
import { DealGraph as DealGraphType, Interval, YAxis } from "store/types";

import "./MeasureGraph.styles.scss";

offlineExporting(Highcharts);
exporting(Highcharts);
exportData(Highcharts);
fullscreen(Highcharts);

interface MeasureGraphProps {
  loading: boolean;
  isBenchmark?: boolean;
  graphData?: DealGraphType;
  interval: Interval;
  chartRef?: React.Ref<HighchartsReactRefObject>;
}

const dateTransform = {
  month: (dateString: string) => dateToMonth(dateString),
  quarter: (dateString: string) => dateToQuarterly(dateString),
  year: (dateString: string) => dateToYear(dateString)
};

const dateToMonth = (dateString: string) => {
  const date = new Date(dateString);
  const month = new Intl.DateTimeFormat("en-US", { month: "short" }).format(date);
  const year = date.getFullYear();

  return `${month} ${year}`;
};

const dateToYear = (dateString: string) => {
  const date = new Date(dateString);
  const year = date.getFullYear();

  return year.toString();
};

const dateToQuarterly = (dateString: string) => {
  const date = new Date(dateString);
  const year = date.getFullYear();
  const quarter = Math.floor((date.getMonth() + 3) / 3);

  return `Q${quarter} ${year}`;
};

export const MeasureGraph: React.FC<MeasureGraphProps> = ({ loading, isBenchmark, graphData, interval, chartRef }) => {
  const formatNumeric = useFormatNumericHook();

  const options: Highcharts.Options = {
    title: {
      text: undefined
    },
    exporting: { enabled: false },
    tooltip: {
      shared: true,
      useHTML: true,
      outside: true,
      stickOnContact: true,
      formatter: function () {
        const points = this.points || [];
        const rows = points
          .map(
            (point) =>
              `<div class="measure-graph__tooltip-row">
                 <span class="measure-graph__tooltip-marker" style="color:${point.color}">●</span>
                 <span class="measure-graph__tooltip-name">${point.series.name}:</span>
                 <b>${formatNumeric({ value: point.y ?? 0 })}</b>
               </div>`
          )
          .join("");

        return `<div class="measure-graph__tooltip">
                  <div class="measure-graph__tooltip-header">${this.x}</div>
                  ${rows}
                </div>`;
      }
    },
    legend: {
      itemStyle: {
        fontSize: isBenchmark ? "14px" : "10px"
      }
    },
    plotOptions: {
      column: {
        stacking: "normal"
      }
    },
    series: [{ data: [], type: "column" }],
    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 500
          },
          chartOptions: {
            legend: {
              layout: "horizontal",
              align: "center",
              verticalAlign: "bottom"
            }
          }
        }
      ]
    },
    credits: { enabled: false }
  };

  const addColorsToYAxis = (yAxis: YAxis, color: string) => {
    return {
      ...yAxis,
      labels: {
        ...yAxis.labels,
        style: {
          color,
          fontSize: "14px"
        }
      },
      title: {
        ...yAxis.title,
        text: isBenchmark ? null : yAxis.title.text,
        useHTML: true
      }
    };
  };

  const lightBlue = "#5d7782";
  const darkBlue = "#0a3142";

  const columnColors = [
    "#ECD9B6",
    "#26A6AE",
    "#066471",
    "#F1C57B",
    "#E89960",
    "#D46535",
    "#803C20",
    "#CFBF84",
    "#DBE2AD",
    "#96C59B"
  ];

  const getOptions = (graphData: DealGraphType, interval: Interval): Highcharts.Options => {
    const formattedXAxis = graphData.xAxis[0].categories.map(dateTransform[interval]);

    const yAxis: Highcharts.Options["yAxis"] = [addColorsToYAxis(graphData.yAxis[0], lightBlue)];

    const multipleColumns = graphData.series.filter((s) => s.type === "column").length > 1;

    if (!isBenchmark && graphData.yAxis[0] && yAxis[0].title) {
      yAxis[0].title.text = `
        <div style="display: flex; align-items: center; font-size: 16px; color: ${lightBlue}; margin-bottom: 20px">
          <div style="margin-right: 20px; width: 17px; height: 17px; background-color: ${
            multipleColumns ? "none" : columnColors[0]
          }; border-radius: 50%;"></div>
          ${graphData.yAxis[0].title.text}
        </div>`;
    }

    if (graphData.yAxis[1]) {
      yAxis.push(addColorsToYAxis(graphData.yAxis[1], lightBlue));
    }

    if (!isBenchmark && yAxis[1] && yAxis[1].title) {
      yAxis[1].title.text = `
        <div style="display: flex; align-items: center; font-size: 16px; color: ${lightBlue}; margin-bottom: 20px">
          ${graphData.yAxis[1].title.text}
          <div style="margin-left: 20px; width: 17px; height: 17px; background-color: ${darkBlue}; border-radius: 50%;"></div>
        </div>`;
    }

    // const marker = {
    //   lineWidth: 2,
    //   lineColor: darkBlue,
    //   fillColor: "#fff",
    //   radius: 4
    // };

    if (isBenchmark && yAxis[1]) {
      yAxis[1].visible = false;
    }

    const series = graphData.series.map((seriesData, index) => {
      if (seriesData.type === "spline") {
        return { ...seriesData, color: lightBlue };
      } else if (seriesData.type === "column") {
        return { ...seriesData, borderRadius: 5, color: columnColors[index % columnColors.length] };
      }
      return seriesData;
    });

    return {
      ...options,
      xAxis: graphData && [
        { categories: formattedXAxis, crosshair: true, labels: { style: { color: lightBlue, fontSize: "14px" } } }
      ],
      yAxis: yAxis,
      series
    };
  };
  const graphOptions = graphData ? getOptions(graphData, interval) : {};

  return loading ? (
    <div className="measure-graph__loader">
      <CircularProgress size={80} />
    </div>
  ) : (
    <div className="measure-graph__chart-block">
      {/* <Box> */}
      <HighchartsReact
        containerProps={{ style: { height: "100%" } }}
        highcharts={Highcharts}
        options={graphOptions}
        ref={chartRef}
      />
      {/* </Box> */}
    </div>
  );
};
